/**
 * Lovable Import — Control Panel + Wizard.
 *
 * Architektura: čistě MCP přes Python proxy (POST /mcp). Žádné REST endpointy.
 * Komponenty:
 *   <LovableControlPanel>   — list view, grouping by lovable_hash, per-row akce
 *   <LovableImportWizard>   — modal s 7-fázovým state machine flow
 *   <LovablePromoteModal>   — tier change (sandbox|staging|production)
 *   <LovableCompareModal>   — cloud vs lokál rows diff
 *
 * Backend tools: apphost.lovable.{list,analyze,provision,deploy,promote,compare,refresh_data}
 *                supabase.lovable_import {mode: schema|data}
 *
 * State management: TanStack Query — list cached, mutace invalidují.
 */
import { themedConfirm } from '../../lib/themed-dialogs';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Modal,
  StatCard,
  StatsGrid,
} from '../../components/ui';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  Database,
  ExternalLink,
  FlaskConical,
  GitBranch,
  Loader2,
  Play,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  applyOperationPhase,
  plannedStack,
  StackFlowGraphic,
  stackFromWizardSpec,
} from '../../components/StackFlowGraphic';
import { operationPollInterval } from '../../lib/bounded-polling';

// ── Types — odpovídají apphost.lovable.list response ────────────────────────

type LovableTier = 'sandbox' | 'staging' | 'production';
type LovableState =
  | 'analyzed'
  | 'provisioned'
  | 'schema_imported'
  | 'data_imported'
  | 'deployed'
  | 'approved'
  | 'backed_up';

interface LovableImportItem {
  id: string;
  apphost_name: string;
  project_id: string | null;
  tier: LovableTier;
  state: LovableState;
  lovable_hash: string;
  local_hash: string;
  origin_repo: string;
  git_branch: string;
  cloud_supabase_url: string;
  local_supabase_name: string | null;
  apphost_instance: string | null;
  apphost_domain: string | null;
  web_url: string;
  api_url: string;
  studio_url: string;
  has_service_role: boolean;
  has_jwt_secret: boolean;
  has_git_token: boolean;
  is_adopted: boolean;
  applied_migrations_count: number;
  last_data_pull_at: string | null;
  last_data_pull_mode: string | null;
  backup_gitlab_url: string | null;
  created_at: string;
  updated_at: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const STATES_ORDER: LovableState[] = [
  'analyzed',
  'provisioned',
  'schema_imported',
  'data_imported',
  'deployed',
  'approved',
  'backed_up',
];

const TIER_STYLE: Record<LovableTier, { bg: string; text: string; label: string }> = {
  production: {
    bg: 'bg-emerald-600/20 border-emerald-500/40',
    text: 'text-emerald-400',
    label: 'PROD',
  },
  staging: { bg: 'bg-amber-600/20 border-amber-500/40', text: 'text-amber-400', label: 'STAG' },
  sandbox: { bg: 'bg-sky-600/20 border-sky-500/40', text: 'text-sky-400', label: 'SAND' },
};

const STATE_LABEL: Record<LovableState, string> = {
  analyzed: 'Analyzed',
  provisioned: 'Provisioned',
  schema_imported: 'Schema OK',
  data_imported: 'Data OK',
  deployed: 'Deployed',
  approved: 'Approved',
  backed_up: 'Backed up',
};

// ── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!iso) return t('apphostLovable.never');
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return t('apphostLovable.justNow');
  if (diffMs < 3_600_000) {
    return t('apphostLovable.minutesAgo', { count: Math.floor(diffMs / 60_000) });
  }
  if (diffMs < 86_400_000) {
    return t('apphostLovable.hoursAgo', { count: Math.floor(diffMs / 3_600_000) });
  }
  return t('apphostLovable.daysAgo', { count: Math.floor(diffMs / 86_400_000) });
}

function groupByHash(items: LovableImportItem[]): Map<string, LovableImportItem[]> {
  const m = new Map<string, LovableImportItem[]>();
  for (const it of items) {
    const arr = m.get(it.lovable_hash) ?? [];
    arr.push(it);
    m.set(it.lovable_hash, arr);
  }
  // Sort imports v každé skupině: prod → staging → sandbox
  const tierRank: Record<LovableTier, number> = { production: 0, staging: 1, sandbox: 2 };
  for (const arr of m.values()) {
    arr.sort((a, b) =>
      tierRank[a.tier] - tierRank[b.tier] ||
      a.created_at.localeCompare(b.created_at)
    );
  }
  return m;
}

// ─── Control Panel — main exported component ────────────────────────────────

export default function LovableControlPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showWizard, setShowWizard] = useState(() => searchParams.get('wizard') === '1');
  useEffect(() => {
    if (searchParams.get('wizard') === '1') setShowWizard(true);
  }, [searchParams]);
  const [resumeTarget, setResumeTarget] = useState<LovableImportItem | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<LovableImportItem | null>(null);
  const [compareTarget, setCompareTarget] = useState<LovableImportItem | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['lovable-list'],
    queryFn: () =>
      api.mcp<{ ok: boolean; count: number; items: LovableImportItem[] }>(
        'apphost.lovable.list',
        {},
      ),
    refetchInterval: 15_000,
  });

  const items = data?.items ?? [];
  const grouped = groupByHash(items);

  const counts = {
    total: items.length,
    production: items.filter((i) => i.tier === 'production').length,
    staging: items.filter((i) => i.tier === 'staging').length,
    sandbox: items.filter((i) => i.tier === 'sandbox').length,
  };

  return (
    <div className='space-y-6'>
      <StatsGrid>
        <StatCard label={t('apphostLovable.totalImports')} value={counts.total} icon={Wand2} />
        <StatCard
          label='Production'
          value={counts.production}
          icon={ShieldCheck}
          variant={counts.production > 0 ? 'success' : undefined}
        />
        <StatCard label='Staging' value={counts.staging} icon={TrendingUp} />
        <StatCard label='Sandbox' value={counts.sandbox} icon={FlaskConical} />
      </StatsGrid>

      <div className='flex justify-between items-center gap-2'>
        <div className='text-xs text-gray-500'>
          {grouped.size > 0
            ? t('apphostLovable.cloudProjectsSummary', {
              projects: grouped.size,
              imports: counts.total,
            })
            : ''}
        </div>
        <div className='flex gap-2'>
          <Button variant='ghost' size='icon' onClick={() => refetch()}>
            <RefreshCw size={13} />
          </Button>
          <button
            onClick={() => {
              setResumeTarget(null);
              setShowWizard(true);
            }}
            className='flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 text-sm font-medium transition-colors'
          >
            <Wand2 size={14} /> {t('apphostLovable.newLovableImport')}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className='py-12 text-center text-gray-500 text-sm'>{t('apphostLovable.loading')}</div>
      )}
      {error && (
        <div className='flex gap-2 items-center text-red-400 text-sm p-4 bg-red-500/10 rounded-xl border border-red-500/20'>
          <AlertTriangle size={14} />
          {(error as Error).message}
        </div>
      )}
      {!isLoading && grouped.size === 0 && !error && (
        <EmptyState
          icon={Wand2}
          title={t('apphostLovable.emptyTitle')}
          description={t('apphostLovable.emptyDescription')}
        />
      )}

      <div className='space-y-4'>
        {Array.from(grouped.entries()).map(([hash, group]) => (
          <ProjectGroupCard
            key={hash}
            hash={hash}
            imports={group}
            onResume={(item) => {
              setResumeTarget(item);
              setShowWizard(true);
            }}
            onPromote={setPromoteTarget}
            onCompare={setCompareTarget}
            onChanged={() => qc.invalidateQueries({ queryKey: ['lovable-list'] })}
          />
        ))}
      </div>

      {showWizard && (
        <LovableImportWizard
          resume={resumeTarget ?? undefined}
          onClose={() => setShowWizard(false)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['lovable-list'] });
            setShowWizard(false);
          }}
        />
      )}
      {promoteTarget && (
        <LovablePromoteModal
          target={promoteTarget}
          allInGroup={grouped.get(promoteTarget.lovable_hash) ?? []}
          onClose={() => setPromoteTarget(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['lovable-list'] });
            setPromoteTarget(null);
          }}
        />
      )}
      {compareTarget && (
        <LovableCompareModal
          target={compareTarget}
          onClose={() => setCompareTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Project group card (collapsible per lovable_hash) ──────────────────────

function ProjectGroupCard(
  { hash, imports, onPromote, onCompare, onChanged, onResume }: {
    hash: string;
    imports: LovableImportItem[];
    onResume: (item: LovableImportItem) => void;
    onPromote: (i: LovableImportItem) => void;
    onCompare: (i: LovableImportItem) => void;
    onChanged: () => void;
  },
) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const repoName = imports[0]?.origin_repo.split('/').slice(-1)[0]?.replace('.git', '') ?? '?';
  const prod = imports.find((i) => i.tier === 'production');

  return (
    <Card>
      <CardContent>
        <button
          onClick={() => setOpen(!open)}
          className='w-full flex items-center gap-3 text-left'
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <div className='flex-1'>
            <div className='flex items-center gap-3'>
              <code className='text-xs font-mono text-purple-400'>{hash}</code>
              <span className='text-sm text-gray-300'>{repoName}</span>
              <span className='ml-auto text-xs text-gray-500'>
                {t('apphostLovable.importCount', { count: imports.length })}
              </span>
            </div>
            {prod && (
              <div className='text-[11px] text-gray-500 mt-0.5'>
                {t('apphostLovable.productionLabel')}: {prod.apphost_name} ·{' '}
                <a
                  href={prod.web_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-400 hover:underline'
                  onClick={(e) => e.stopPropagation()}
                >
                  {prod.web_url.replace('https://', '')}{' '}
                  <ExternalLink size={10} className='inline' />
                </a>
              </div>
            )}
          </div>
        </button>

        {open && (
          <div className='mt-3 space-y-2 pl-6'>
            {imports.map((i) => (
              <ImportRowCard
                key={i.id}
                item={i}
                onResume={() => onResume(i)}
                onPromote={() => onPromote(i)}
                onCompare={() => onCompare(i)}
                onChanged={onChanged}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Single import row ──────────────────────────────────────────────────────

function ImportRowCard(
  { item, onPromote, onCompare, onChanged, onResume }: {
    item: LovableImportItem;
    onResume: () => void;
    onPromote: () => void;
    onCompare: () => void;
    onChanged: () => void;
  },
) {
  const { t } = useTranslation();
  const tier = TIER_STYLE[item.tier];
  const discardMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; manage_url?: string }>(
        'apphost.lovable.discard',
        { import_id: item.id },
      ),
    onSuccess: (r) => {
      if (r.ok) onChanged();
      else if (r.manage_url) window.location.assign(r.manage_url);
      else toast.error(r.error ?? t('apphostLovable.toastRefreshBuildFailed'));
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const stateIdx = STATES_ORDER.indexOf(item.state);
  const progress = ((stateIdx + 1) / STATES_ORDER.length) * 100;

  // Re-pull data — wraps refresh_data
  const [pullPasswordOpen, setPullPasswordOpen] = useState(false);
  const [pullPassword, setPullPassword] = useState('');

  const refreshDataMut = useMutation({
    mutationFn: (cloud_db_password: string) =>
      api.mcp<{ ok: boolean; error?: string; duration_seconds?: number }>(
        'apphost.lovable.refresh_data',
        { apphost_name: item.apphost_name, cloud_db_password },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('apphostLovable.toastDataPullOk', { seconds: r.duration_seconds }));
        onChanged();
      } else {
        toast.error(r.error ?? t('apphostLovable.toastRefreshFailed'));
      }
      setPullPasswordOpen(false);
      setPullPassword('');
    },
    onError: (e) => {
      toast.error((e as Error).message);
      setPullPasswordOpen(false);
    },
  });

  // Adopt — registruje existing orphan apphost do acm.apphost_instances
  const adoptMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; app_port?: number; container_status?: string }>(
        'apphost.adopt',
        {
          name: item.apphost_instance ?? `${item.apphost_name}-frontend`,
          type: 'react',
          from_lovable_import: true,
        },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          t('apphostLovable.toastAdoptOk', { port: r.app_port, container: r.container_status }),
        );
        onChanged();
      } else {
        toast.error(r.error ?? t('apphostLovable.toastAdoptFailed'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Refresh build — git pull + npm build + dist swap + restart (žádný client.ts rewrite)
  const refreshBuildMut = useMutation({
    mutationFn: () =>
      api.mcp<
        {
          ok: boolean;
          error?: string;
          head_commit?: string;
          duration_ms?: number;
          actions?: string[];
        }
      >(
        'apphost.lovable.refresh_build',
        { apphost_name: item.apphost_name },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          t('apphostLovable.toastBuildOk', {
            head: r.head_commit,
            seconds: Math.round((r.duration_ms ?? 0) / 1000),
          }),
        );
        onChanged();
      } else {
        toast.error(r.error ?? t('apphostLovable.toastRefreshBuildFailed'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='border border-gray-800 rounded-xl bg-gray-950/50 p-3 space-y-2'>
      <div className='flex items-center gap-2 flex-wrap'>
        <div
          className={`px-2 py-0.5 rounded text-[10px] font-bold border ${tier.bg} ${tier.text}`}
        >
          {tier.label}
        </div>
        <div className='font-medium text-sm text-white'>{item.apphost_name}</div>
        {item.project_id && (
          <code className='text-[10px] text-gray-500 font-mono'>{item.project_id}</code>
        )}
        <div className='ml-auto flex items-center gap-2'>
          {item.is_adopted
            ? (
              <span
                title={t('apphostLovable.adoptedTitle')}
                className='px-1.5 py-0.5 rounded text-[10px] font-bold border bg-emerald-600/20 border-emerald-500/40 text-emerald-400'
              >
                {t('apphostLovable.adoptedBadge')}
              </span>
            )
            : (
              <span
                title={t('apphostLovable.orphanTitle')}
                className='px-1.5 py-0.5 rounded text-[10px] font-bold border bg-amber-600/20 border-amber-500/40 text-amber-400'
              >
                {t('apphostLovable.orphanBadge')}
              </span>
            )}
          <Badge variant={stateIdx >= 4 ? 'success' : 'default'}>{STATE_LABEL[item.state]}</Badge>
        </div>
      </div>

      {/* Progress bar */}
      <div className='h-1 bg-gray-800 rounded-full overflow-hidden'>
        <div
          className='h-full bg-purple-500 transition-all'
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-500'>
        <div className='flex items-center gap-1.5'>
          <GitBranch size={10} />
          <span className='truncate'>{item.git_branch}</span>
        </div>
        <div className='flex items-center gap-1.5'>
          <Database size={10} />
          <span>
            {t('apphostLovable.migrationsCount', { count: item.applied_migrations_count })}
          </span>
        </div>
        <div className='flex items-center gap-1.5'>
          <RefreshCw size={10} />
          <span>{t('apphostLovable.dataLabel')}: {relativeTime(item.last_data_pull_at, t)}</span>
        </div>
      </div>

      <div className='flex items-center gap-1.5 text-[11px]'>
        <a
          href={item.web_url}
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-400 hover:underline truncate'
        >
          {item.web_url.replace('https://', '')} <ExternalLink size={9} className='inline' />
        </a>
      </div>

      <div className='flex items-center gap-1.5 text-[11px] text-gray-500'>
        <GitBranch size={10} />
        <span className='font-mono truncate' title={item.origin_repo}>
          {item.origin_repo.replace(/^https?:\/\/([^@]+@)?/, '')}
        </span>
        {item.has_git_token && (
          <span
            className='ml-1 px-1 py-0.5 rounded text-[9px] bg-gray-800 text-gray-400'
            title={t('apphostLovable.patTitle')}
          >
            PAT ✓
          </span>
        )}
      </div>

      <div className='flex flex-wrap gap-1 pt-1 border-t border-gray-800'>
        {STATES_ORDER.indexOf(item.state) < STATES_ORDER.indexOf('deployed') && (
          <>
            <Button size='sm' variant='secondary' onClick={onResume}>
              {t('apphostLovable.resumeImport')}
            </Button>
            <Button
              size='sm'
              variant='secondary'
              disabled={discardMut.isPending}
              onClick={() => discardMut.mutate()}
            >
              {t('apphostLovable.discardIntent')}
            </Button>
          </>
        )}

        {!item.is_adopted && (
          <button
            onClick={() => adoptMut.mutate()}
            disabled={adoptMut.isPending}
            className='text-[11px] px-2 py-1 rounded bg-amber-600/30 hover:bg-amber-600/50 text-amber-300 border border-amber-500/40 disabled:opacity-50'
            title={t('apphostLovable.adoptButtonTitle')}
          >
            {adoptMut.isPending ? t('apphostLovable.adopting') : t('apphostLovable.adopt')}
          </button>
        )}
        <button
          onClick={() => refreshBuildMut.mutate()}
          disabled={refreshBuildMut.isPending || !item.is_adopted}
          className='text-[11px] px-2 py-1 rounded bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 border border-blue-500/40 disabled:opacity-50'
          title={item.is_adopted
            ? t('apphostLovable.refreshBuildTitle')
            : t('apphostLovable.requiresAdoptFirst')}
        >
          <RefreshCw size={10} className='inline mr-1' />
          {refreshBuildMut.isPending
            ? t('apphostLovable.building')
            : t('apphostLovable.updateFromGit')}
        </button>
        <button
          onClick={() => setPullPasswordOpen(true)}
          className='text-[11px] px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300'
          disabled={item.state === 'analyzed' || item.state === 'provisioned'}
        >
          <RefreshCw size={10} className='inline mr-1' /> {t('apphostLovable.refreshData')}
        </button>
        <button
          onClick={onCompare}
          title={t('apphostLovable.compareTitle')}
          className='text-[11px] px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300'
          disabled={item.state === 'analyzed' || item.state === 'provisioned'}
        >
          {t('apphostLovable.compareCloud')}
        </button>
        <button
          onClick={onPromote}
          title={t('apphostLovable.promoteTitle')}
          className='text-[11px] px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300'
        >
          {t('apphostLovable.promoteTier')}
        </button>
      </div>

      {pullPasswordOpen && (
        <Modal
          isOpen={pullPasswordOpen}
          onClose={() => setPullPasswordOpen(false)}
          title={t('apphostLovable.refreshDataModalTitle')}
        >
          <div className='space-y-3'>
            <div className='text-xs text-gray-400'>
              {t('apphostLovable.pgDumpPrefix')}{' '}
              <code className='text-purple-400'>{item.cloud_supabase_url}</code>{' '}
              {t('apphostLovable.pgDumpSuffix')}
            </div>
            <input
              type='password'
              value={pullPassword}
              onChange={(e) => setPullPassword(e.target.value)}
              placeholder={t('apphostLovable.cloudDbPassword')}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
            />
            <div className='flex justify-end gap-2'>
              <Button variant='ghost' onClick={() => setPullPasswordOpen(false)}>
                {t('apphostLovable.cancel')}
              </Button>
              <Button
                variant='primary'
                onClick={() => refreshDataMut.mutate(pullPassword)}
                disabled={!pullPassword || refreshDataMut.isPending}
              >
                {refreshDataMut.isPending
                  ? (
                    <>
                      <Loader2 size={12} className='animate-spin inline mr-1' />{' '}
                      {t('apphostLovable.pulling')}
                    </>
                  )
                  : t('apphostLovable.pull')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Promote modal ──────────────────────────────────────────────────────────

function LovablePromoteModal(
  { target, allInGroup, onClose, onDone }: {
    target: LovableImportItem;
    allInGroup: LovableImportItem[];
    onClose: () => void;
    onDone: () => void;
  },
) {
  const { t } = useTranslation();
  const [toTier, setToTier] = useState<LovableTier>('staging');
  const [autoDemote, setAutoDemote] = useState(false);

  const existingProd = allInGroup.find(
    (i) => i.tier === 'production' && i.apphost_name !== target.apphost_name,
  );
  const willCollide = toTier === 'production' && !!existingProd;

  const promoteMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; hash_changed?: boolean }>(
        'apphost.lovable.promote',
        {
          apphost_name: target.apphost_name,
          to_tier: toTier,
          demote_existing_production: autoDemote,
        },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          r.hash_changed
            ? t('apphostLovable.toastPromoteOkRedeployed', { from: target.tier, to: toTier })
            : t('apphostLovable.toastPromoteOk', { from: target.tier, to: toTier }),
        );
        onDone();
      } else {
        toast.error(r.error ?? t('apphostLovable.toastPromoteFailed'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('apphostLovable.promoteModalTitle', { name: target.apphost_name })}
    >
      <div className='space-y-4'>
        <div className='text-xs text-gray-400'>
          {t('apphostLovable.currentTier')}:{' '}
          <span className='text-white font-medium'>{target.tier}</span>
        </div>

        <div>
          <div className='text-xs text-gray-400 mb-2'>{t('apphostLovable.newTier')}:</div>
          <div className='grid grid-cols-3 gap-2'>
            {(['sandbox', 'staging', 'production'] as LovableTier[]).map((tpl) => (
              <button
                key={tpl}
                onClick={() => setToTier(tpl)}
                disabled={tpl === target.tier}
                className={`py-2 rounded-lg border text-xs font-bold transition-colors ${
                  toTier === tpl
                    ? `${TIER_STYLE[tpl].bg} ${TIER_STYLE[tpl].text}`
                    : tpl === target.tier
                    ? 'border-gray-800 bg-gray-900 text-gray-700 cursor-not-allowed'
                    : 'border-gray-700 text-gray-500 hover:text-white'
                }`}
              >
                {TIER_STYLE[tpl].label}
              </button>
            ))}
          </div>
        </div>

        {willCollide && (
          <div className='flex gap-2 items-start p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300'>
            <AlertTriangle size={13} className='flex-shrink-0 mt-0.5' />
            <div>
              <div className='font-semibold mb-1'>{t('apphostLovable.prodImportExists')}</div>
              <div className='text-amber-400/80'>
                <code>{existingProd!.apphost_name}</code> ({existingProd!.project_id})
              </div>
              <label className='flex items-center gap-2 mt-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={autoDemote}
                  onChange={(e) => setAutoDemote(e.target.checked)}
                />
                <span>{t('apphostLovable.autoDemote')}</span>
              </label>
            </div>
          </div>
        )}

        <div className='text-[11px] text-gray-500'>
          {target.tier !== toTier &&
            ((target.tier === 'production') !== (toTier === 'production')) && (
            <div className='flex gap-1 items-center'>
              <RefreshCw size={10} />
              <span>{t('apphostLovable.localHashChange')}</span>
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('apphostLovable.cancel')}</Button>
          <Button
            variant='primary'
            onClick={async () => {
              // Extra potvrzení pro promote DO production — to může re-deploy
              // instance + collide s existing prod (= auto-demote druhého importu).
              if (toTier === 'production') {
                const msg = willCollide
                  ? t('apphostLovable.confirmPromoteProdCollide', { name: target.apphost_name })
                  : t('apphostLovable.confirmPromoteProd', { name: target.apphost_name });
                if (!(await themedConfirm(msg))) return;
              }
              promoteMut.mutate();
            }}
            disabled={toTier === target.tier || (willCollide && !autoDemote) ||
              promoteMut.isPending}
          >
            {promoteMut.isPending
              ? (
                <>
                  <Loader2 size={12} className='animate-spin inline mr-1' />{' '}
                  {t('apphostLovable.promoting')}
                </>
              )
              : t('apphostLovable.promote')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Compare modal ─────────────────────────────────────────────────────────

interface CompareTable {
  schema: string;
  table: string;
  cloud_rows: number | null;
  local_rows: number | null;
  diff: number | null;
  status: 'sync' | 'cloud_only' | 'local_only' | 'mismatch' | 'error';
}

interface CompareResult {
  ok: boolean;
  cloud_error?: string | null;
  summary?: {
    total_tables: number;
    sync: number;
    mismatch: number;
    cloud_only: number;
    local_only: number;
    error: number;
  };
  tables?: CompareTable[];
  error?: string;
}

function LovableCompareModal(
  { target, onClose }: { target: LovableImportItem; onClose: () => void },
) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const compareQuery = useQuery({
    queryKey: ['lovable-compare', target.apphost_name],
    queryFn: () =>
      api.mcp<CompareResult>('apphost.lovable.compare', {
        apphost_name: target.apphost_name,
        cloud_db_password: password,
      }),
    enabled: submitted && !!password,
    retry: false,
  });

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('apphostLovable.compareModalTitle', { name: target.apphost_name })}
    >
      <div className='space-y-4'>
        {!submitted && (
          <>
            <div className='text-xs text-gray-400'>
              {t('apphostLovable.cloudLabel')}:{' '}
              <code className='text-purple-400'>{target.cloud_supabase_url}</code>
            </div>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('apphostLovable.cloudDbPassword')}
              className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500'
            />
            <div className='flex justify-end gap-2'>
              <Button variant='ghost' onClick={onClose}>{t('apphostLovable.cancel')}</Button>
              <Button variant='primary' onClick={() => setSubmitted(true)} disabled={!password}>
                {t('apphostLovable.compare')}
              </Button>
            </div>
          </>
        )}

        {submitted && compareQuery.isLoading && (
          <div className='py-8 text-center text-gray-500'>
            <Loader2 size={20} className='animate-spin mx-auto mb-2' />
            <div className='text-xs'>{t('apphostLovable.computingCounts')}</div>
          </div>
        )}

        {submitted && compareQuery.isError && (
          <div className='text-xs text-red-400 p-3 bg-red-500/10 rounded-lg border border-red-500/20'>
            {(compareQuery.error as Error).message}
          </div>
        )}

        {submitted && compareQuery.data && <CompareTable result={compareQuery.data} />}
      </div>
    </Modal>
  );
}

function CompareTable({ result }: { result: CompareResult }) {
  const { t } = useTranslation();
  if (!result.ok) {
    return (
      <div className='text-xs text-red-400 p-3 bg-red-500/10 rounded-lg'>
        {result.error ?? t('apphostLovable.compareFailed')}
      </div>
    );
  }

  const tables = result.tables ?? [];
  const summary = result.summary;

  return (
    <div className='space-y-3'>
      {result.cloud_error && (
        <div className='text-xs text-amber-400 p-2 bg-amber-500/10 rounded-lg border border-amber-500/20'>
          {t('apphostLovable.cloudConnFailed')}: {result.cloud_error}
        </div>
      )}
      {summary && (
        <div className='grid grid-cols-5 gap-1 text-[10px] text-center'>
          <div className='p-1.5 bg-gray-800 rounded'>
            <div className='text-white font-bold'>{summary.total_tables}</div>
            <div className='text-gray-500'>{t('apphostLovable.summaryTotal')}</div>
          </div>
          <div className='p-1.5 bg-emerald-500/10 rounded'>
            <div className='text-emerald-400 font-bold'>{summary.sync}</div>
            <div className='text-gray-500'>sync</div>
          </div>
          <div className='p-1.5 bg-amber-500/10 rounded'>
            <div className='text-amber-400 font-bold'>{summary.mismatch}</div>
            <div className='text-gray-500'>mismatch</div>
          </div>
          <div className='p-1.5 bg-blue-500/10 rounded'>
            <div className='text-blue-400 font-bold'>{summary.cloud_only}</div>
            <div className='text-gray-500'>{t('apphostLovable.summaryCloudOnly')}</div>
          </div>
          <div className='p-1.5 bg-purple-500/10 rounded'>
            <div className='text-purple-400 font-bold'>{summary.local_only}</div>
            <div className='text-gray-500'>{t('apphostLovable.summaryLocalOnly')}</div>
          </div>
        </div>
      )}
      <div className='border border-gray-800 rounded-lg overflow-hidden max-h-96 overflow-y-auto'>
        <table className='w-full text-[11px]'>
          <thead className='bg-gray-900 sticky top-0'>
            <tr className='text-left text-gray-500'>
              <th className='px-2 py-1.5'>{t('apphostLovable.colTable')}</th>
              <th className='px-2 py-1.5 text-right'>Cloud</th>
              <th className='px-2 py-1.5 text-right'>{t('apphostLovable.colLocal')}</th>
              <th className='px-2 py-1.5 text-right'>Diff</th>
            </tr>
          </thead>
          <tbody>
            {tables.map((row) => (
              <tr key={`${row.schema}.${row.table}`} className='border-t border-gray-800'>
                <td className='px-2 py-1.5 font-mono'>
                  <span className='text-gray-500'>{row.schema}.</span>
                  <span className='text-white'>{row.table}</span>
                </td>
                <td className='px-2 py-1.5 text-right text-gray-300'>
                  {row.cloud_rows !== null ? row.cloud_rows.toLocaleString() : '—'}
                </td>
                <td className='px-2 py-1.5 text-right text-gray-300'>
                  {row.local_rows !== null ? row.local_rows.toLocaleString() : '—'}
                </td>
                <td
                  className={`px-2 py-1.5 text-right font-mono ${
                    row.status === 'sync'
                      ? 'text-emerald-400'
                      : row.status === 'mismatch'
                      ? 'text-amber-400'
                      : row.status === 'cloud_only'
                      ? 'text-blue-400'
                      : row.status === 'local_only'
                      ? 'text-purple-400'
                      : 'text-gray-500'
                  }`}
                >
                  {row.status === 'sync'
                    ? '✓'
                    : row.status === 'mismatch'
                    ? `${row.diff! > 0 ? '+' : ''}${row.diff}`
                    : row.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Adresa repozitáře v podobě, ke které má platforma pověření.
 *
 * GitLab v API vrací `http_url_to_repo` se svou vlastní veřejnou adresou.
 * Připojení k němu ale může být vedené pod jiným jménem téhož serveru — a
 * pověření se hledá podle hostitele. Když se liší, vyhrává hostitel připojení;
 * cesta k projektu zůstává.
 */
export function repoUrlForConnection(httpUrl: string, connectionHost?: string): string {
  if (!connectionHost) return httpUrl;
  try {
    const repo = new URL(httpUrl);
    const conn = new URL(
      connectionHost.startsWith('http') ? connectionHost : `https://${connectionHost}`,
    );
    if (repo.host === conn.host) return httpUrl;
    repo.protocol = conn.protocol;
    repo.host = conn.host;
    return repo.toString();
  } catch {
    return httpUrl;
  }
}

// ─── Import Wizard — 7-step state machine flow ────────────────────────────

interface WizardForm {
  apphost_name: string;
  git_repo: string;
  git_branch: string;
  git_token: string;
  tier: LovableTier;
  cloud_db_password: string;
  jwt_secret: string;
  source: 'lovable-cloud' | 'git' | 'acm-supabase';
  existing_supabase: string;
  clone_of_project_id?: string;
}

interface WizardGitlabProject {
  id: number;
  name: string;
  path: string;
  http_url: string;
  default_branch: string | null;
  connection_id?: string;
  host?: string;
}

export function LovableImportWizard(
  {
    onClose,
    onDone,
    enableCodable = false,
    initial,
    resume,
  }: {
    onClose: () => void;
    onDone: () => void;
    enableCodable?: boolean;
    resume?: LovableImportItem;
    initial?: Partial<Pick<WizardForm, 'apphost_name' | 'git_repo' | 'git_branch' | 'source'>>;
  },
) {
  const { t } = useTranslation();
  const [step, setStep] = useState<
    | 'config'
    | 'orch'
    | 'analyze'
    | 'provision'
    | 'schema'
    | 'data'
    | 'deploy'
    | 'done'
  >('config');
  const [orchSpec, setOrchSpec] = useState<{ services?: Record<string, unknown> } | null>(null);
  const [orchOpId, setOrchOpId] = useState<string | null>(null);
  const [orchError, setOrchError] = useState<string | null>(null);
  const [gitlabSearch, setGitlabSearch] = useState('');
  const [showGitlabPicker, setShowGitlabPicker] = useState(true);
  // Jméno předvyplněné z vybraného repozitáře — ať je vidět, odkud se vzalo.
  const [prefilledFromRepo, setPrefilledFromRepo] = useState(false);
  const [form, setForm] = useState<WizardForm>({
    apphost_name: '',
    git_repo: '',
    git_branch: 'main',
    git_token: '',
    tier: 'sandbox',
    cloud_db_password: '',
    jwt_secret: '',
    source: initial?.source ?? 'lovable-cloud',
    existing_supabase: '',
    ...(initial?.apphost_name ? { apphost_name: initial.apphost_name } : {}),
    ...(initial?.git_repo ? { git_repo: initial.git_repo } : {}),
    ...(initial?.git_branch ? { git_branch: initial.git_branch } : {}),
    ...(resume
      ? {
        apphost_name: resume.apphost_name,
        git_repo: resume.origin_repo,
        git_branch: resume.git_branch,
        tier: resume.tier,
      }
      : {}),
  });
  const [log, setLog] = useState<Array<{ phase: string; ok: boolean; detail: string }>>([]);
  // Opakovaný pokus navazuje na existující identitu; klon vyžaduje výslovnou volbu.
  const [resuming, setResuming] = useState<LovableImportItem | null>(resume ?? null);
  const existingQ = useQuery({
    queryKey: ['lovable-imports-names'],
    queryFn: () => api.mcp<{ items?: LovableImportItem[] }>('apphost.lovable.list', {}),
  });
  const ownerImports = useQuery({
    queryKey: ['lovable-orchestrator-imports'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        environments?: Array<{
          name: string;
          project_id: string;
          import_source?: string;
          is_local?: boolean;
        }>;
      }>('orchestrator.orch_list', {}),
    enabled: step === 'config',
    retry: false,
  });
  const existingOwnerImports = (ownerImports.data?.environments ?? []).filter((
    env,
  ) =>
    env.import_source === 'lovable' && env.is_local && env.project_id &&
    !(existingQ.data?.items ?? []).some((item) => item.project_id === env.project_id)
  );
  const slugTaken = !!form.apphost_name &&
    (existingQ.data?.items ?? []).some((i) => i.apphost_name === form.apphost_name);
  const selectResume = async (name: string) => {
    const items = existingQ.data?.items ?? (await api.mcp<{ items: LovableImportItem[] }>(
      'apphost.lovable.list',
      {},
    )).items;
    const item = items.find((i) => i.apphost_name === name);
    if (!item) return;
    setResuming(item);
    setForm((f) => ({
      ...f,
      apphost_name: item.apphost_name,
      git_repo: item.origin_repo,
      git_branch: item.git_branch,
      tier: item.tier,
      source: 'lovable-cloud',
      clone_of_project_id: undefined,
    }));
    setLog([]);
    setRunResult(null);
    setStep('config');
  };
  const confirmClone = async (projectId: string) => {
    if (!await themedConfirm(t('apphostLovable.confirmClone'))) return;
    setResuming(null);
    setForm((f) => ({ ...f, clone_of_project_id: projectId }));
    setRunResult(null);
    setStep('config');
  };
  const discardImport = async (name: string) => {
    const data = await existingQ.refetch();
    const item = data.data?.items?.find((i) => i.apphost_name === name);
    if (!item) return;
    try {
      const result = await api.mcp<{ ok: boolean; error?: string; manage_url?: string }>(
        'apphost.lovable.discard',
        { import_id: item.id },
      );
      if (!result.ok) {
        if (result.manage_url) window.location.assign(result.manage_url);
        else toast.error(result.error ?? t('apphostLovable.toastRefreshBuildFailed'));
        return;
      }
      setRunResult(null);
      setResuming(null);
      setLog([]);
      await existingQ.refetch();
      setStep('config');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const beginCloud = () => {
    if (!resuming) {
      setStep('analyze');
      return;
    }
    const next: Record<LovableState, typeof step> = {
      analyzed: 'provision',
      provisioned: 'schema',
      schema_imported: 'data',
      data_imported: 'deploy',
      deployed: 'done',
      approved: 'done',
      backed_up: 'done',
    };
    setStep(next[resuming.state]);
  };
  const [completion, setCompletion] = useState<OneShotResult | null>(null);
  const [queueingCompletion, setQueueingCompletion] = useState(false);
  const completionHistory = useQuery({
    queryKey: ['lovable-completion', completion?.task_id],
    queryFn: () =>
      api.mcp<{ runs: TaskHistoryRow[] }>('tasks.task_history', {
        id: completion!.task_id,
        limit: 1,
      }),
    enabled: !!completion?.task_id,
    refetchInterval: (q) => q.state.data?.runs?.[0]?.finished_at ? false : 3000,
  });
  const completionRun = completionHistory.data?.runs?.[0];
  const [runResult, setRunResult] = useState<Record<string, unknown> | null>(
    null,
  );

  const runStep = async (phase: string, mcp: () => Promise<unknown>) => {
    try {
      const r = await mcp() as {
        ok?: boolean;
        error?: string;
        message?: string;
        completion?: OneShotResult;
      };
      const ok = r.ok !== false;
      setLog((
        l,
      ) => [...l, {
        phase,
        ok,
        detail: ok ? (r.message ?? 'OK') : (r.error ?? 'failed'),
      }]);
      if (r.completion) setCompletion(r.completion);
      setRunResult(r as Record<string, unknown>);
      return { ok, detail: r.error };
    } catch (e) {
      const detail = (e as Error).message;
      setLog((l) => [...l, { phase, ok: false, detail }]);
      return { ok: false, detail };
    }
  };

  const inp =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  const stepOrder: string[] = form.source === 'lovable-cloud'
    ? ['config', 'analyze', 'provision', 'schema', 'data', 'deploy', 'done']
    : ['config', 'orch', 'analyze', 'schema', 'data', 'deploy', 'done'];
  const stepIdx = stepOrder.indexOf(step);

  const phases = form.source === 'lovable-cloud'
    ? [
      { id: 'analyze', label: t('apphostLovable.phaseAnalyze', 'Analyze') },
      { id: 'provision', label: t('apphostLovable.phaseProvision', 'Provision') },
      { id: 'schema', label: t('apphostLovable.phaseSchema', 'Schema') },
      { id: 'data', label: t('apphostLovable.phaseData', 'Data') },
      { id: 'deploy', label: t('apphostLovable.phaseDeploy', 'Deploy') },
    ]
    : [
      { id: 'orch', label: t('apphostLovable.phaseOrch', 'Orchestrátor') },
      { id: 'analyze', label: t('apphostLovable.phaseAnalyze', 'Analyze') },
      { id: 'schema', label: t('apphostLovable.phaseSchema', 'Schema') },
      { id: 'data', label: t('apphostLovable.phaseData', 'Data') },
      { id: 'deploy', label: t('apphostLovable.phaseDeploy', 'Deploy') },
    ];

  const gitlabProjectsQ = useQuery({
    queryKey: ['codable-user-projects', gitlabSearch],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        projects?: WizardGitlabProject[];
        error?: string;
        setup_required?: string;
      }>('codable.gitlab_user_projects', {
        search: gitlabSearch.trim() || undefined,
        limit: 50,
      }),
    staleTime: 30_000,
  });
  const sbListQ = useQuery({
    queryKey: ['supabase-list-for-lovable-wizard'],
    queryFn: () =>
      api.mcp<{ ok?: boolean; instances?: Array<{ name: string; status?: string }> }>(
        'supabase.list',
        {},
      ),
    enabled: form.source === 'acm-supabase',
    staleTime: 30_000,
  });

  const orchOpQ = useQuery({
    queryKey: ['orchestrator-project-operation', orchOpId],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        operation?: {
          id: string;
          slug: string;
          status: string;
          phase: string;
          progress: number;
          error?: string | null;
        };
      }>('orchestrator.project_operation_get', { operation_id: orchOpId }),
    enabled: !!orchOpId && step === 'orch',
    refetchInterval: (q) =>
      operationPollInterval(q.state.data?.operation?.status, q.state.dataUpdateCount),
  });
  const orchOp = orchOpQ.data?.operation;
  const orchStack = applyOperationPhase(
    orchSpec ? stackFromWizardSpec(orchSpec) : plannedStack({
      frontend: 'react',
      backend: 'supabase',
      publish: true,
      backup: true,
      gitHint: form.git_repo.split('/').pop()?.replace(/\.git$/i, ''),
    }),
    orchOp?.phase,
    orchOp?.status,
  );

  const startOrch = async () => {
    setOrchError(null);
    setStep('orch');
    try {
      if (orchOpId) {
        const retried = await api.mcp<{ ok: boolean; error?: string }>(
          'orchestrator.project_operation_retry',
          { operation_id: orchOpId },
        );
        if (!retried.ok) setOrchError(retried.error ?? t('apphostLovable.toastRefreshBuildFailed'));
        await orchOpQ.refetch();
        return;
      }
      const wiz = await api.mcp<{
        ok: boolean;
        error?: string;
        spec?: { services?: Record<string, unknown> };
      }>('orchestrator.orch_wizard', {
        mode: 'lovable',
        git_repo: form.git_repo,
        git_branch: form.git_branch,
        name: form.apphost_name,
        ...(form.git_token ? { git_token: form.git_token } : {}),
      });
      if (!wiz.ok || !wiz.spec) {
        setOrchError(wiz.error ?? 'orch_wizard selhal');
        return;
      }
      setOrchSpec(wiz.spec);
      const created = await api.mcp<{
        ok: boolean;
        operation_id?: string;
        error?: string;
        setup_required?: string;
      }>('orchestrator.project_create', {
        request: {
          version: 1,
          source: {
            consumer: enableCodable ? 'codable' : 'apphost',
            channel: 'ui-lovable-import',
          },
          project: { slug: form.apphost_name },
          repository: {
            provider: 'gitlab',
            mode: 'create',
            visibility: 'internal',
            initialize_with_readme: false,
          },
          template: { kind: 'repository', source_url: form.git_repo },
          runtime: {
            frontend: { type: 'react', run_mode: 'dev', build_cmd: 'npm ci && npm run build' },
            backend: {
              type: 'supabase',
              ...(form.source === 'acm-supabase' && form.existing_supabase
                ? { instance: form.existing_supabase }
                : {}),
            },
          },
          publication: { frontend: { enabled: true } },
          codable: {
            enabled: enableCodable,
            source: 'lovable',
            origin: { git_url: form.git_repo },
          },
        },
      });
      if (!created.ok || !created.operation_id) {
        setOrchError(created.error ?? created.setup_required ?? 'project_create selhal');
        return;
      }
      setOrchOpId(created.operation_id);
    } catch (e) {
      setOrchError((e as Error).message);
    }
  };

  return (
    <Modal
      open
      size='xl'
      onClose={onClose}
      title={t('apphostLovable.wizardTitle')}
    >
      {/* Progress */}
      <div className='flex items-center gap-1 mb-5'>
        {phases.map((p, i) => {
          const phaseIdx = stepOrder.indexOf(p.id as typeof step);
          return (
            <div key={p.id} className='flex items-center gap-1 flex-1'>
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  phaseIdx < stepIdx
                    ? 'bg-emerald-600 text-white'
                    : phaseIdx === stepIdx
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-600'
                }`}
              >
                {phaseIdx < stepIdx ? <CheckCircle2 size={12} /> : i + 1}
              </div>
              {i < phases.length - 1 && (
                <div
                  className={`h-0.5 flex-1 ${
                    phaseIdx < stepIdx ? 'bg-emerald-600' : 'bg-gray-800'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className='min-h-[280px] space-y-3'>
        {/* CONFIG step — collect inputs */}
        {step === 'config' && (
          <>
            {ownerImports.error && <p role='alert'>{ownerImports.error.message}</p>}
            {existingOwnerImports.length > 0 && (
              <div className='rounded-xl border border-gray-700 p-3 space-y-2'>
                <p>{t('apphostLovable.ownerImports')}</p>
                {existingOwnerImports.map((project) => (
                  <div key={project.project_id} className='flex flex-wrap items-center gap-3'>
                    <span>{project.name}</span>
                    <span className='text-xs text-gray-400'>{project.project_id}</span>
                    <a className='underline text-blue-400' href={`/modules/orchestrator?env=${encodeURIComponent(project.name)}`}>
                      {t('apphostLovable.continueOwnerImport')}
                    </a>
                  </div>
                ))}
              </div>
            )}
            {
              /* Import z cizí infrastruktury — řekni to na začátku, ne až
                když něco nesedí. */
            }
            <div className='rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 space-y-1 mb-3'>
              <div className='flex items-center gap-2 text-xs font-medium text-amber-200'>
                <AlertTriangle size={13} className='flex-shrink-0' />
                {t('codable.lovableDisclaimerTitle')}
              </div>
              <p className='text-[11px] leading-relaxed text-amber-100/80'>
                {t('codable.lovableDisclaimerBody')}
              </p>
              <p className='text-[11px] leading-relaxed text-amber-100/80'>
                {t('codable.lovableDisclaimerAgent')}
              </p>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-2'>
              {[
                {
                  id: 'lovable-cloud' as const,
                  icon: <Cloud size={16} />,
                  title: t('apphostLovable.srcCloud', 'Lovable Cloud'),
                  desc: t(
                    'apphostLovable.srcCloudDesc',
                    'Git export + schema/data z cloud Supabase',
                  ),
                },
                {
                  id: 'git' as const,
                  icon: <GitBranch size={16} />,
                  title: t('apphostLovable.srcGit', 'Git (GitLab / GitHub)'),
                  desc: t('apphostLovable.srcGitDesc', 'Jen kód — stack založí Orchestrátor'),
                },
                {
                  id: 'acm-supabase' as const,
                  icon: <Database size={16} />,
                  title: t('apphostLovable.srcSupabase', 'ACM Supabase'),
                  desc: t(
                    'apphostLovable.srcSupabaseDesc',
                    'FE + lokální Supabase, bez cloud dumpu',
                  ),
                },
              ].map((s) => (
                <button
                  key={s.id}
                  type='button'
                  onClick={() => setForm((f) => ({ ...f, source: s.id }))}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    form.source === s.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-gray-800 bg-gray-950 hover:border-gray-700'
                  }`}
                >
                  <div className='text-purple-300 mb-2'>{s.icon}</div>
                  <div className='text-sm font-semibold text-white'>{s.title}</div>
                  <div className='text-[11px] text-gray-500 mt-1'>{s.desc}</div>
                </button>
              ))}
            </div>
            <label className='block'>
              {
                /*
                Dřív se pole jmenovalo `apphost_name` a nápověda mluvila o „env".
                Z toho nešlo poznat to podstatné: jestli pojmenovává NOVOU věc
                u nás, nebo odkazuje na zdrojové repo — a jestli se stejným
                jménem něco nesrazí v GitLabu (Filip 2026-09-08). Nesrazí:
                zdrojové repo se jen čte. Proto pole říká, co vznikne.
              */
              }
              <div className='text-xs text-gray-400 mb-1'>
                {t('apphostLovable.newNameLabel', 'Název nového prostředí')} *
              </div>
              <input
                value={form.apphost_name}
                disabled={!!resuming}
                onChange={(e) => setForm((f) => ({ ...f, apphost_name: e.target.value }))}
                placeholder='nemovitostnik-test1'
                className={inp}
              />
              <div className='text-[10px] text-gray-500 mt-1'>
                {t(
                  'apphostLovable.newNameHint',
                  'Pojmenuje to, co vznikne u nás — zdrojový repozitář se jen čte a nemění.',
                )}
              </div>
              {form.apphost_name && (
                <div className='text-[10px] text-gray-500 mt-1 font-mono'>
                  {t('apphostLovable.newNamePreview', {
                    defaultValue:
                      'Vznikne: prostředí lov-{{name}} · databáze lov-{{name}}-db · instance {{name}}-frontend',
                    name: form.apphost_name,
                  })}
                </div>
              )}
              {prefilledFromRepo && (
                <div className='text-[10px] text-gray-500 mt-1'>
                  {t(
                    'apphostLovable.newNameFromRepo',
                    'Předvyplněno podle vybraného repozitáře. Můžeš nechat i přepsat — se jménem repozitáře v GitLabu se to nesrazí.',
                  )}
                </div>
              )}
              {/* Duplicita se má říct TEĎ, ne až selháním v půlce stavby. */}
              {resuming && (
                <p className='text-sm text-gray-200 mt-2'>
                  {t('apphostLovable.resumingProject', {
                    name: resuming.apphost_name,
                    id: resuming.project_id ?? '',
                  })}
                </p>
              )}
              {slugTaken && !resuming && (
                <div className='text-[10px] text-amber-300 mt-1'>
                  {t('apphostLovable.slugTaken', { name: form.apphost_name })}
                  <Button
                    size='sm'
                    variant='secondary'
                    onClick={() => void selectResume(form.apphost_name)}
                  >
                    {t('apphostLovable.resumeImport')}
                  </Button>
                </div>
              )}
            </label>

            <label className='block'>
              <div className='text-xs text-gray-400 mb-1'>Git repo URL *</div>
              <input
                value={form.git_repo}
                onChange={(e) => setForm((f) => ({ ...f, git_repo: e.target.value }))}
                placeholder='https://gitlab.solanaenterprise.cz/user/repo'
                className={inp}
              />
            </label>

            <div className='rounded-xl border border-gray-800 bg-gray-950/40 p-3 space-y-2'>
              <button
                type='button'
                onClick={() => setShowGitlabPicker((v) => !v)}
                className='flex w-full items-center justify-between text-xs text-gray-300'
              >
                <span className='flex items-center gap-1.5'>
                  <GitBranch size={12} />
                  {t('apphostLovable.pickFromGitlab', 'Vybrat repo z propojených GitLabů')}
                </span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showGitlabPicker ? 'rotate-180' : ''}`}
                />
              </button>
              {showGitlabPicker && (
                <>
                  <div className='relative'>
                    <Search
                      size={12}
                      className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600'
                    />
                    <input
                      className={`${inp} pl-8`}
                      placeholder={t('codable.searchYourProjects', 'Hledat v mých projektech…')}
                      value={gitlabSearch}
                      onChange={(e) => setGitlabSearch(e.target.value)}
                    />
                  </div>
                  {gitlabProjectsQ.isLoading && (
                    <div className='flex items-center gap-2 text-xs text-gray-500 py-2'>
                      <Loader2 size={12} className='animate-spin' />
                      {t('codable.loadingYourProjects', 'Načítám projekty…')}
                    </div>
                  )}
                  {gitlabProjectsQ.data?.setup_required === 'gitlab_credentials' && (
                    <div className='text-xs text-amber-300'>
                      {t('codable.missingGitlabToken', 'Chybí GitLab token')}
                    </div>
                  )}
                  <div className='max-h-40 overflow-y-auto space-y-1'>
                    {(gitlabProjectsQ.data?.projects ?? []).map((p) => (
                      <button
                        key={`${p.connection_id ?? 'gl'}:${p.id}`}
                        type='button'
                        onClick={() => {
                          const slug = (p.path.split('/').pop() || p.name)
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, '-')
                            .replace(/^-+|-+$/g, '');
                          setForm((f) => {
                            const takeSlug = !f.apphost_name;
                            if (takeSlug) setPrefilledFromRepo(true);
                            return {
                              ...f,
                              // Adresu bereme z připojení, ne z toho, co o sobě
                              // hlásí GitLab. Týž server má obojí — připojení
                              // jede na 10.119.251.19, ale sám o sobě říká
                              // gitlab.solanaenterprise.cz. Pod druhým jménem
                              // k němu platforma nemá pověření a klon skončil
                              // na „could not read Username" (2026-09-08).
                              git_repo: repoUrlForConnection(p.http_url, p.host),
                              git_branch: p.default_branch || f.git_branch,
                              apphost_name: f.apphost_name || slug,
                            };
                          });
                        }}
                        className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${
                          form.git_repo === p.http_url
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-gray-800 hover:border-gray-700'
                        }`}
                      >
                        <div className='text-xs text-gray-200 truncate'>{p.name}</div>
                        <div className='text-[10px] text-gray-500 font-mono truncate'>
                          {p.host ? `${p.host.replace(/^https?:\/\//, '')} · ` : ''}
                          {p.path}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {form.source === 'acm-supabase' && (
              <label className='block'>
                <div className='text-xs text-gray-400 mb-1'>
                  {t('apphostLovable.existingSupabase', 'Existující ACM Supabase (volitelně)')}
                </div>
                <select
                  className={inp}
                  value={form.existing_supabase}
                  onChange={(e) => setForm((f) => ({ ...f, existing_supabase: e.target.value }))}
                >
                  <option value=''>
                    {t('apphostLovable.existingSupabaseNew', 'Nová instance')}
                  </option>
                  {(sbListQ.data?.instances ?? []).map((inst) => (
                    <option key={inst.name} value={inst.name}>
                      {inst.name}
                      {inst.status ? ` (${inst.status})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className='grid grid-cols-2 gap-3'>
              <label className='block'>
                <div className='text-xs text-gray-400 mb-1'>{t('apphostLovable.branch')}</div>
                <input
                  value={form.git_branch}
                  onChange={(e) => setForm((f) => ({ ...f, git_branch: e.target.value }))}
                  className={inp}
                />
              </label>
              <label className='block'>
                <div className='text-xs text-gray-400 mb-1'>
                  {t('apphostLovable.gitTokenOptional')}
                </div>
                <input
                  type='password'
                  value={form.git_token}
                  onChange={(e) => setForm((f) => ({ ...f, git_token: e.target.value }))}
                  placeholder='glpat-...'
                  className={inp}
                />
              </label>
            </div>

            {
              /* Tier z obrazovky pryč: ptal se na NAŠI kolizi subdomén slovy,
                která uživateli nic neříkají. Identitu nese slug výš — ten se
                odvodí z repozitáře, zkontroluje na duplicitu a dá přepsat
                (Filip 2026-09-09). `form.tier` zůstává jako odvozená hodnota. */
            }
            <details className='text-xs'>
              <summary className='cursor-pointer text-gray-500 hover:text-white'>
                {t('apphostLovable.jwtSummary')}
              </summary>
              <div className='mt-2 space-y-1'>
                <input
                  type='password'
                  value={form.jwt_secret}
                  onChange={(e) => setForm((f) => ({ ...f, jwt_secret: e.target.value }))}
                  placeholder='Cloud Supabase JWT secret (32+ chars)'
                  className={inp}
                />
                <div className='text-[10px] text-gray-500'>
                  {t('apphostLovable.jwtHint')}
                </div>
              </div>
            </details>

            <div className='rounded-xl border border-gray-800 bg-gray-950/40 p-4'>
              <StackFlowGraphic
                chainOnly
                title={t('apphostLovable.stackPreview', 'Co se postaví')}
                services={plannedStack({
                  frontend: 'react',
                  backend: 'supabase',
                  publish: true,
                  backup: true,
                  gitHint: form.git_repo.split('/').pop()?.replace(/\.git$/i, ''),
                })}
              />
            </div>
            {form.source === 'lovable-cloud' && (
              <label className='block'>
                <div className='text-xs text-gray-400 mb-1'>
                  {t('apphostLovable.cloudDbPassword', 'Heslo cloud Supabase (schema + data)')}
                </div>
                <input
                  type='password'
                  value={form.cloud_db_password}
                  onChange={(e) => setForm((f) => ({ ...f, cloud_db_password: e.target.value }))}
                  className={inp}
                  placeholder={t('apphostLovable.cloudDbPasswordPlaceholder')}
                />
                {
                  /* Důsledek, ne jen název pole: tohle rozhoduje, jestli přijdou
                    data, nebo zůstane prázdná databáze. */
                }
                <div className='text-[10px] text-gray-500 mt-1'>
                  {t('apphostLovable.cloudDbPasswordHint')}
                </div>
              </label>
            )}
            <div className='flex flex-col sm:flex-row justify-end gap-2 pt-2'>
              <Button variant='ghost' onClick={onClose}>{t('apphostLovable.cancel')}</Button>
              {form.source === 'lovable-cloud'
                ? (
                  <Button
                    variant='primary'
                    onClick={beginCloud}
                    disabled={!form.apphost_name || !form.git_repo}
                  >
                    {resuming
                      ? t('apphostLovable.resumeImport')
                      : t('apphostLovable.startCloud', 'Spustit wizard (schema + data)')}
                    <ArrowRight size={12} className='inline ml-1' />
                  </Button>
                )
                : (
                  <Button
                    variant='primary'
                    onClick={() => void startOrch()}
                    disabled={!form.apphost_name || !form.git_repo}
                  >
                    {t('apphostLovable.startOrch', 'Založit přes Orchestrátor')}
                    <ArrowRight size={12} className='inline ml-1' />
                  </Button>
                )}
            </div>
          </>
        )}

        {step === 'orch' && (
          <div className='space-y-3'>
            <div className='text-sm text-gray-300'>
              {orchOp
                ? `${orchOp.slug} · ${orchOp.phase} · ${orchOp.progress}%`
                : t('apphostLovable.orchStarting', 'Orchestrátor skládá spec a zakládá služby…')}
            </div>
            {orchError && <div className='text-sm text-red-300'>{orchError}</div>}
            {orchOp?.error && <div className='text-sm text-red-300'>{orchOp.error}</div>}
            <StackFlowGraphic services={orchStack} />
            {orchOp?.status === 'completed' && (
              <div className='flex justify-end gap-2'>
                <Button variant='secondary' onClick={() => {
                  window.location.assign(`/modules/orchestrator?env=${encodeURIComponent(orchOp.slug)}`);
                }}>
                  {t(
                    'apphostLovable.advancedCloud',
                    'Pokročilé: schema + data z cloudu',
                  )}
                </Button>
                <Button variant='primary' onClick={onDone}>
                  {t('apphostLovable.done')}
                </Button>
              </div>
            )}
            {orchOp?.status === 'failed' && (
              <Button
                variant='secondary'
                onClick={() => void startOrch()}
              >
                {t('codable.retryDraft', 'Zkusit znovu')}
              </Button>
            )}
          </div>
        )}

        {runResult?.code === 'LOVABLE_IMPORT_DECISION_REQUIRED' && (
          <div className='space-y-3 rounded-xl border border-gray-600 bg-gray-950 p-4 text-gray-100'>
            <p>{t('apphostLovable.existingSource')}</p>
            {(runResult.candidates as Array<
              { apphost_name: string; project_id: string | null; state: string }
            >).map((item) => (
              <div key={item.apphost_name} className='flex flex-wrap items-center gap-2'>
                <span>{item.apphost_name} · {STATE_LABEL[item.state as LovableState]}</span>
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={() =>
                    void selectResume(item.apphost_name)}
                >
                  {t('apphostLovable.resumeImport')}
                </Button>
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={() =>
                    void discardImport(item.apphost_name)}
                >
                  {t('apphostLovable.discardIntent')}
                </Button>
                {item.project_id && (
                  <>
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() => void confirmClone(item.project_id!)}
                    >
                      {t('apphostLovable.createClone')}
                    </Button>
                    <a
                      className='underline text-gray-100'
                      href={`/modules/orchestrator?env=${encodeURIComponent(item.apphost_name)}`}
                    >
                      {t('apphostLovable.manageUnfinished')}
                    </a>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Phase steps — auto-run + show result */}
        {step !== 'config' && step !== 'done' && step !== 'orch' && (
          <PhaseRunner
            phase={step}
            form={form}
            log={log}
            onComplete={async (success) => {
              if (!success) return;
              const next = stepOrder[stepIdx + 1];
              if (next === 'data' && !form.cloud_db_password) {
                // Pause to collect cloud DB password before data phase
                setStep('data');
              } else if (next) {
                setStep(next as typeof step);
              }
            }}
            runStep={runStep}
            onPasswordChange={(p) => setForm((f) => ({ ...f, cloud_db_password: p }))}
          />
        )}

        {completion && completionRun?.status !== 'ok' && (
          <div className='rounded-lg border border-amber-800 bg-amber-950/20 p-3 text-xs text-gray-100'>
            <p>{t('apphostLovable.publicationSeparate')}</p>
            {(completion.error || completionRun?.error) && (
              <p className='mt-1'>{completion.error || completionRun?.error}</p>
            )}
            {completion.task_id && (
              <a className='underline' href='/modules/tasks'>
                {t('apphostLovable.publicationTask')}
              </a>
            )}
            <Button
              variant='secondary'
              onClick={async () => {
                setQueueingCompletion(true);
                try {
                  const result = await api.mcp<OneShotResult>(
                    'tasks.run_one_shot',
                    {
                      module: 'orchestrator',
                      tool: 'orchestrator.orch_apply',
                      args: JSON.stringify({
                        name: form.apphost_name,
                        phase: 'completion',
                      }),
                      label: `Lovable publication: ${form.apphost_name}`,
                      group_name: 'lovable-completion',
                    },
                  );
                  setCompletion(result);
                } catch (e) {
                  setCompletion({ ok: false, error: (e as Error).message });
                } finally {
                  setQueueingCompletion(false);
                }
              }}
              disabled={queueingCompletion ||
                (!!completion.task_id && !completionRun?.finished_at)}
            >
              {t('apphostLovable.retryPublication')}
            </Button>
          </div>
        )}

        {step === 'done' && (
          <div className='space-y-3'>
            <div className='flex flex-col items-center py-4'>
              <CheckCircle2 size={32} className='text-emerald-400 mb-2' />
              <div className='text-sm font-semibold text-white'>
                {t('apphostLovable.importDone')}
              </div>
              <div className='text-xs text-gray-500'>
                {t('apphostLovable.importDoneDetail', { name: form.apphost_name })}
              </div>
            </div>
            {runResult && typeof runResult.web_url === 'string' && (
              <a
                href={runResult.web_url as string}
                target='_blank'
                rel='noopener noreferrer'
                className='block text-center text-xs text-blue-400 hover:underline'
              >
                {runResult.web_url as string} <ExternalLink size={10} className='inline' />
              </a>
            )}
            <div className='border border-gray-800 rounded-lg p-2 max-h-40 overflow-y-auto'>
              {log.map((l, i) => (
                <div key={i} className='text-[11px] flex gap-2'>
                  <span className={l.ok ? 'text-emerald-400' : 'text-red-400'}>
                    {l.ok ? '✓' : '✗'}
                  </span>
                  <span className='text-gray-400'>{l.phase}:</span>
                  <span className='text-gray-300'>{l.detail}</span>
                </div>
              ))}
            </div>
            <div className='flex justify-end'>
              <Button variant='primary' onClick={onDone}>{t('apphostLovable.done')}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── PhaseRunner — auto-runs current phase, displays progress ──────────────

// Long-running fáze běží přes tasks.run_one_shot — UI poll task_history
// místo synchronní mcp call (jinak browser fetch timeout na 5-20 min).
// Provision také stahuje image a staví FE: běží jako sledovaná úloha.
const ASYNC_PHASES: Record<
  'provision' | 'schema' | 'data' | 'deploy',
  { module: string; tool: string }
> = {
  provision: { module: 'apphost', tool: 'apphost.lovable.provision' },
  schema: { module: 'supabase', tool: 'lovable_import' },
  data: { module: 'supabase', tool: 'lovable_import' },
  deploy: { module: 'apphost', tool: 'lovable.deploy' },
};

interface OneShotResult {
  ok: boolean;
  task_id?: string;
  run_id?: number;
  error?: string;
}

interface TaskHistoryRow {
  id: string; // run_id (vrací se jako string z task_history)
  status: 'running' | 'ok' | 'error';
  started_at: string;
  finished_at: string | null;
  output: string | null;
  error: string | null;
  triggered_by: string;
}

export function PhaseRunner({
  phase,
  form,
  log,
  onComplete,
  runStep,
  onPasswordChange,
}: {
  phase: 'analyze' | 'provision' | 'schema' | 'data' | 'deploy';
  form: WizardForm;
  log: Array<{ phase: string; ok: boolean; detail: string }>;
  onComplete: (success: boolean) => void;
  runStep: (
    phase: string,
    mcp: () => Promise<unknown>,
  ) => Promise<{ ok: boolean; detail?: string }>;
  onPasswordChange: (p: string) => void;
}) {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(phase === 'data' && !form.cloud_db_password);
  const [tempPassword, setTempPassword] = useState('');
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [boundProjectId, setBoundProjectId] = useState<string | null>(null);
  const [pendingProvision, setPendingProvision] = useState<
    { name: string; project_id: string } | null
  >(null);

  // A completed command can leave an ongoing deployment. Follow the owner's
  // live state, never elapsed time, and resume the same project under this user.
  const provisionStatus = useQuery({
    queryKey: ['lovable-provision-status', pendingProvision?.project_id, pendingProvision?.name],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        project_id?: string;
        core_ready?: boolean;
        core_pending?: boolean;
        core_failures?: Array<{ service: string; error: string }>;
        error?: string;
      }>('orchestrator.orch_status', { name: pendingProvision!.name, live: true }),
    enabled: !!pendingProvision,
    refetchInterval: 5000,
    retry: false,
  });
  useEffect(() => {
    if (!pendingProvision) return;
    const current = provisionStatus.data;
    const error = provisionStatus.error;
    if (!current && !error) return;
    if (!error && current?.ok && current.project_id === pendingProvision.project_id) {
      if (current.core_ready) {
        setPendingProvision(null);
        setRunning(false);
        setAttempt((n) => n + 1);
        return;
      }
      if (current.core_pending) return;
    }
    setPendingProvision(null);
    setRunning(false);
    runStep(phase, async () => ({
      ok: false,
      error: error?.message ?? current?.error ??
        (current?.project_id !== pendingProvision.project_id
          ? t('apphostLovable.provisionIdentityChanged')
          : current?.core_failures?.map((f) => `${f.service}: ${f.error}`).join('; ') ||
            t('apphostLovable.provisionStatusUnavailable')),
    })).then(() => onComplete(false));
  }, [provisionStatus.dataUpdatedAt, provisionStatus.errorUpdatedAt, pendingProvision]); // eslint-disable-line react-hooks/exhaustive-deps

  // Args pro long-running fáze (předané do tasks.run_one_shot)
  const phaseArgs: Record<keyof typeof ASYNC_PHASES, Record<string, unknown>> = {
    provision: {
      apphost_name: form.apphost_name,
      ...(boundProjectId ? { project_id: boundProjectId } : {}),
      ...(form.jwt_secret ? { jwt_secret: form.jwt_secret } : {}),
    },
    schema: { apphost_name: form.apphost_name, mode: 'schema' },
    data: {
      apphost_name: form.apphost_name,
      mode: 'data',
      cloud_db_password: form.cloud_db_password,
    },
    deploy: { apphost_name: form.apphost_name },
  };

  // Krátké fáze — direct mcp call (rychlejší než tasks overhead)
  const directFn: Record<'analyze' | 'provision', () => Promise<unknown>> = {
    analyze: () =>
      api.mcp('apphost.lovable.analyze', {
        apphost_name: form.apphost_name,
        git_repo: form.git_repo,
        git_branch: form.git_branch,
        git_token: form.git_token || undefined,
        tier: form.tier,
        ...(form.clone_of_project_id ? { clone_of_project_id: form.clone_of_project_id } : {}),
        force_reclone: true,
      }),
    provision: () =>
      api.mcp('apphost.lovable.provision', {
        apphost_name: form.apphost_name,
        ...(form.jwt_secret ? { jwt_secret: form.jwt_secret } : {}),
      }),
  };

  // Polling task status — pro async fáze. Refetch 2s, dokud status='running'.
  // task_history accepts 'id' (task_id) + limit; vrací { runs: [...] } desc by started_at.
  const taskHistory = useQuery({
    queryKey: ['task-history', activeTaskId],
    queryFn: () =>
      api.mcp<{ ok: boolean; runs: TaskHistoryRow[] }>('tasks.task_history', {
        id: activeTaskId!,
        limit: 1,
      }),
    enabled: !!activeTaskId,
    refetchInterval: (q) => {
      const r = q.state.data?.runs?.[0];
      // Stop polling jakmile finished (status != 'running')
      return r && r.status !== 'running' ? false : 2000;
    },
  });

  // Reakce na dokončení async fáze — když status přejde z running na ok|error
  useEffect(() => {
    if (!activeTaskId) return;
    const r = taskHistory.data?.runs?.[0];
    if (!r || r.status === 'running') return;
    setActiveTaskId(null);
    setRunning(false);
    let result: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(r.output ?? '{}');
      if (parsed && typeof parsed === 'object') result = parsed;
    } catch { /* starší textový výstup */ }
    if (
      phase === 'provision' && r.status === 'ok' && result.pending === true &&
      typeof result.env === 'string' && typeof result.project_id === 'string'
    ) {
      setPendingProvision({ name: result.env, project_id: result.project_id });
      setBoundProjectId(result.project_id);
      setRunning(true);
      return;
    }
    runStep(phase, async () => ({
      ...result,
      ok: r.status === 'ok' && result.ok !== false,
      error: r.error ?? result.error,
    })).then((res) => {
      onComplete(res.ok);
    });
  }, [taskHistory.data, activeTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-run on phase enter (unless data phase needs password)
  useEffect(() => {
    if (running || needsPassword) return;
    setRunning(true);

    if (phase in ASYNC_PHASES) {
      // Async cesta: vyrobí task, vrací task_id, pak polling task_history.
      const cfg = ASYNC_PHASES[phase as keyof typeof ASYNC_PHASES];
      api.mcp<OneShotResult>('tasks.run_one_shot', {
        module: cfg.module,
        tool: cfg.tool,
        args: JSON.stringify(phaseArgs[phase as keyof typeof ASYNC_PHASES]),
        label: `Lovable ${phase}: ${form.apphost_name}`,
        group_name: 'lovable-import',
      })
        .then((r) => {
          if (!r.ok || !r.task_id) {
            setRunning(false);
            runStep(phase, async () => ({ ok: false, error: r.error ?? 'task spawn failed' }))
              .then((res) => onComplete(res.ok));
            return;
          }
          setActiveTaskId(r.task_id); // → spustí useQuery polling
        })
        .catch((e) => {
          setRunning(false);
          runStep(phase, async () => ({ ok: false, error: (e as Error).message }))
            .then((res) => onComplete(res.ok));
        });
    } else {
      // Sync cesta: krátké fáze (analyze, provision) přímo
      runStep(phase, directFn[phase as 'analyze' | 'provision']).then((r) => {
        setRunning(false);
        onComplete(r.ok);
      });
    }
  }, [phase, needsPassword, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (needsPassword) {
    return (
      <div className='space-y-3'>
        <div className='text-xs text-gray-400'>
          {t('apphostLovable.pgDumpNeedsPassword')}
        </div>
        <input
          type='password'
          value={tempPassword}
          onChange={(e) => setTempPassword(e.target.value)}
          placeholder={t('apphostLovable.cloudDbPasswordPlaceholder')}
          className='w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white'
        />
        <div className='flex justify-end'>
          <Button
            variant='primary'
            onClick={() => {
              onPasswordChange(tempPassword);
              setNeedsPassword(false);
            }}
            disabled={!tempPassword}
          >
            {t('apphostLovable.continue')}
          </Button>
        </div>
      </div>
    );
  }

  const elapsed = activeTaskId && taskHistory.data?.runs?.[0]?.started_at
    ? Math.floor((Date.now() - new Date(taskHistory.data.runs[0].started_at).getTime()) / 1000)
    : null;

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2 text-sm'>
        {running
          ? <Loader2 size={14} className='animate-spin text-purple-400' />
          : <Play size={14} className='text-purple-400' />}
        <span className='text-white capitalize'>{phase}…</span>
        {pendingProvision && <span>{t('apphostLovable.provisionWaiting')}</span>}
        {activeTaskId && (
          <>
            <span className='text-[10px] text-gray-500 ml-2 font-mono'>
              task {activeTaskId.slice(0, 8)}
            </span>
            {elapsed !== null && (
              <span className='text-[10px] text-gray-500'>
                ({elapsed}s)
              </span>
            )}
          </>
        )}
      </div>
      {!running && !activeTaskId && !pendingProvision && log[log.length - 1]?.ok === false && (
        <Button onClick={() => setAttempt((n) => n + 1)}>
          <RefreshCw size={14} /> {t('apphostLovable.retryPhase')}
        </Button>
      )}
      <div className='border border-gray-800 rounded-lg p-2 max-h-48 overflow-y-auto bg-gray-950'>
        {log.length === 0 && (
          <div className='text-[11px] text-gray-600'>{t('apphostLovable.starting')}</div>
        )}
        {log.map((l, i) => (
          <div key={i} className='text-[11px] flex gap-2'>
            <span className={l.ok ? 'text-emerald-400' : 'text-red-400'}>
              {l.ok ? '✓' : '✗'}
            </span>
            <span className='text-gray-400'>{l.phase}:</span>
            <span className='text-gray-300'>{l.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
